import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLabs, useLabCategories } from '../hooks';
import {
  labManagerApi,
  sessionManagerApi,
  parseApiError,
} from '../services/apiClient';
import styles from './Labs.module.css';

interface CatalogSettings {
  title: string;
  description: string;
  difficultyLevel: 'beginner' | 'intermediate' | 'advanced';
  estimatedDuration: number;
  category: string;
  baseImage: string;
}
const emptySettings: CatalogSettings = {
  title: '',
  description: '',
  difficultyLevel: 'beginner',
  estimatedDuration: 60,
  category: '',
  baseImage: '',
};

const LabSettings: React.FC<{
  id?: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ id, onClose, onSaved }) => {
  const [form, setForm] = useState<CatalogSettings | null>(
    id ? null : emptySettings,
  );
  const detail = useQuery({
    queryKey: ['labs', 'settings', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const response = await labManagerApi.get(
        `/instructor/labs/${encodeURIComponent(id!)}`,
      );
      const lab = response.data.lab;
      return {
        title: lab.title,
        description: lab.description ?? '',
        difficultyLevel: lab.difficulty_level ?? 'beginner',
        estimatedDuration: lab.estimated_duration ?? 60,
        category: lab.category ?? '',
        baseImage: lab.base_image,
      } as CatalogSettings;
    },
  });
  const current = form ?? detail.data;
  const save = useMutation({
    mutationFn: async () => {
      if (!current) return;
      return id
        ? labManagerApi.patch(`/labs/${encodeURIComponent(id)}`, current)
        : labManagerApi.post('/labs', current);
    },
    onSuccess: onSaved,
  });
  return (
    <section className={styles.editor} aria-labelledby="lab-settings-heading">
      <header>
        <h2 id="lab-settings-heading">
          {id ? 'Edit catalog settings' : 'Create a lab catalog entry'}
        </h2>
        <p>
          Catalog settings do not edit the lab&apos;s authored instructions,
          assessment criteria, or source files. New entries are saved as drafts.
        </p>
      </header>
      {detail.isLoading ? (
        <p role="status">Loading current settings…</p>
      ) : detail.isError ? (
        <div role="alert">
          <p>Couldn&apos;t load the existing lab. Editing is unavailable.</p>
          <button type="button" onClick={() => detail.refetch()}>
            Retry
          </button>
        </div>
      ) : (
        current && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!save.isPending) save.mutate();
            }}
          >
            <label>
              Title
              <input
                autoFocus
                required
                maxLength={255}
                value={current.title}
                onChange={(event) =>
                  setForm({ ...current, title: event.target.value })
                }
              />
            </label>
            <label>
              Description
              <textarea
                rows={3}
                maxLength={10000}
                value={current.description}
                onChange={(event) =>
                  setForm({ ...current, description: event.target.value })
                }
              />
            </label>
            <label>
              Difficulty
              <select
                value={current.difficultyLevel}
                onChange={(event) =>
                  setForm({
                    ...current,
                    difficultyLevel: event.target
                      .value as CatalogSettings['difficultyLevel'],
                  })
                }
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </label>
            <label>
              Estimated duration (minutes)
              <input
                type="number"
                min={1}
                max={10080}
                required
                value={current.estimatedDuration}
                onChange={(event) =>
                  setForm({
                    ...current,
                    estimatedDuration: Number(event.target.value),
                  })
                }
              />
            </label>
            <label>
              Category
              <input
                maxLength={100}
                value={current.category}
                onChange={(event) =>
                  setForm({ ...current, category: event.target.value })
                }
              />
            </label>
            <label>
              Approved runtime image
              <input
                required
                maxLength={255}
                value={current.baseImage}
                onChange={(event) =>
                  setForm({ ...current, baseImage: event.target.value })
                }
              />
              <span>Use the runtime image approved for this lab.</span>
            </label>
            {save.isError && (
              <p role="alert">{parseApiError(save.error).message}</p>
            )}
            <div className={styles.editorActions}>
              <button
                type="submit"
                className={styles.createBtn}
                disabled={save.isPending}
              >
                {save.isPending ? 'Saving…' : 'Save catalog settings'}
              </button>
              <button type="button" onClick={onClose} disabled={save.isPending}>
                Cancel
              </button>
            </div>
          </form>
        )
      )}
    </section>
  );
};

const Labs: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [published, setPublished] = useState('');
  const [page, setPage] = useState(1);
  const [editor, setEditor] = useState<string | 'new' | null>(null);
  const [confirm, setConfirm] = useState<{
    id: string;
    title: string;
    action: 'delete' | 'publish' | 'unpublish';
  } | null>(null);
  const labs = useLabs({
    search: search || undefined,
    category: category || undefined,
    difficulty: (difficulty as CatalogSettings['difficultyLevel']) || undefined,
    isPublished: published ? published === 'published' : undefined,
    page,
    limit: 25,
  });
  const categories = useLabCategories();
  const identity = useQuery({
    queryKey: ['staff', 'identity'],
    queryFn: async () => (await sessionManagerApi.get('/auth/me')).data.user,
  });
  const canEdit = identity.data?.role === 'admin';
  const update = useMutation({
    mutationFn: async () => {
      if (!confirm) return;
      return confirm.action === 'delete'
        ? labManagerApi.delete(`/labs/${encodeURIComponent(confirm.id)}`)
        : labManagerApi.post(
            `/labs/${encodeURIComponent(confirm.id)}/publish`,
            { publish: confirm.action === 'publish' },
          );
    },
    onSuccess: () => {
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: ['labs'] });
    },
  });
  const changeFilter = (setter: (value: string) => void, value: string) => {
    setter(value);
    setPage(1);
  };
  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1>Labs</h1>
          <p>Monitor lab activity and catalog publication.</p>
          {!canEdit && <p>Catalog changes require a platform administrator.</p>}
        </div>
        {canEdit && (
          <button
            type="button"
            className={styles.createBtn}
            onClick={() => setEditor('new')}
          >
            Create lab
          </button>
        )}
      </header>
      {identity.isError && (
        <p role="alert">
          Couldn&apos;t verify your catalog permissions.{' '}
          <button type="button" onClick={() => identity.refetch()}>
            Retry
          </button>
        </p>
      )}
      {editor && canEdit && (
        <LabSettings
          key={editor}
          id={editor === 'new' ? undefined : editor}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            void queryClient.invalidateQueries({ queryKey: ['labs'] });
          }}
        />
      )}
      <div className={styles.filters}>
        <input
          type="search"
          aria-label="Search labs"
          className={styles.searchInput}
          style={{ maxWidth: 320 }}
          placeholder="Search labs…"
          value={search}
          onChange={(event) => changeFilter(setSearch, event.target.value)}
        />
        <select
          aria-label="Filter by category"
          className={styles.select}
          value={category}
          onChange={(event) => changeFilter(setCategory, event.target.value)}
        >
          <option value="">All categories</option>
          {categories.data?.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by difficulty"
          className={styles.select}
          value={difficulty}
          onChange={(event) => changeFilter(setDifficulty, event.target.value)}
        >
          <option value="">All difficulties</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
        <select
          aria-label="Filter by publication"
          className={styles.select}
          value={published}
          onChange={(event) => changeFilter(setPublished, event.target.value)}
        >
          <option value="">All publication states</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>
      {confirm && (
        <section
          className={styles.editor}
          role="alertdialog"
          aria-labelledby="lab-confirm-title"
          aria-describedby="lab-confirm-description"
        >
          <h2 id="lab-confirm-title">
            {confirm.action[0].toUpperCase() + confirm.action.slice(1)}{' '}
            {confirm.title}?
          </h2>
          <p id="lab-confirm-description">
            {confirm.action === 'delete'
              ? 'This permanently removes the catalog entry. Prefer unpublishing when learning records must be retained.'
              : confirm.action === 'publish'
                ? 'Publishing exposes this lab to eligible learners. Confirm that its learning materials and assessments are ready.'
                : 'Unpublishing removes the lab from learner discovery.'}
          </p>
          <div className={styles.editorActions}>
            <button
              type="button"
              className={styles.createBtn}
              disabled={update.isPending}
              onClick={() => update.mutate()}
            >
              {update.isPending ? 'Saving…' : 'Confirm'}
            </button>
            <button
              type="button"
              disabled={update.isPending}
              onClick={() => setConfirm(null)}
            >
              Cancel
            </button>
          </div>
          {update.isError && (
            <p role="alert">{parseApiError(update.error).message}</p>
          )}
        </section>
      )}
      {labs.isLoading ? (
        <p role="status" className={styles.loading}>
          Loading labs…
        </p>
      ) : labs.isError ? (
        <div role="alert" className={styles.loading}>
          <p>Couldn&apos;t load labs.</p>
          <button
            type="button"
            className={styles.createBtn}
            onClick={() => labs.refetch()}
          >
            Retry
          </button>
        </div>
      ) : !labs.data?.data.length ? (
        <p className={styles.loading}>No labs match these filters.</p>
      ) : (
        <>
          <div className={styles.catalogTable}>
            <table>
              <thead>
                <tr>
                  <th scope="col">Lab</th>
                  <th scope="col">Publication</th>
                  <th scope="col">Completed</th>
                  <th scope="col">Pending reviews</th>
                  <th scope="col">Average score</th>
                  {canEdit && <th scope="col">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {labs.data.data.map((lab) => (
                  <tr key={lab.id}>
                    <td>
                      <strong>{lab.title}</strong>
                      <p className={styles.description}>{lab.description}</p>
                      <p>
                        {lab.category || 'Uncategorized'} ·{' '}
                        {lab.difficulty || 'Difficulty not set'}
                      </p>
                    </td>
                    <td>{lab.isPublished ? 'Published' : 'Draft'}</td>
                    <td>{lab.studentsCompleted}</td>
                    <td>
                      <Link
                        to={`/grading?labId=${encodeURIComponent(lab.id)}`}
                        aria-label={`Review ${lab.submissionsPending} pending submissions for ${lab.title}`}
                      >
                        {lab.submissionsPending} pending
                      </Link>
                    </td>
                    <td>
                      {lab.averageScore == null ? '—' : `${lab.averageScore}%`}
                    </td>
                    {canEdit && (
                      <td>
                        <div className={styles.editorActions}>
                          <button
                            type="button"
                            onClick={() => setEditor(lab.id)}
                          >
                            Edit settings
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirm({
                                id: lab.id,
                                title: lab.title,
                                action: lab.isPublished
                                  ? 'unpublish'
                                  : 'publish',
                              })
                            }
                          >
                            {lab.isPublished ? 'Unpublish' : 'Publish'}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirm({
                                id: lab.id,
                                title: lab.title,
                                action: 'delete',
                              })
                            }
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <nav className={styles.editorActions} aria-label="Lab pages">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span>
              Page {page} of {labs.data.pagination.totalPages} ·{' '}
              {labs.data.pagination.total} labs
            </span>
            <button
              type="button"
              disabled={page >= labs.data.pagination.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </nav>
        </>
      )}
    </div>
  );
};

export default Labs;
